import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import {
  DEFAULT_DETENT,
  detentHeightPx,
  nextDetent,
  snapDetent,
  type SheetDetent,
} from "@/lib/sheet/detent";
import { restoreFocus, useFocusRestoreTarget } from "@/lib/a11y/focusRestore";

/**
 * Fraction of the sheet's own height a drag must cover before release dismisses.
 *
 * DESIGN.md §11.5 #5: 「拖到 sheet-bottom 30% 处 release 关闭」. Not a fixed pixel
 * count — a 90vh sheet and a 40vh sheet need different travel before the gesture
 * reads as deliberate, and a constant would dismiss the short one on a twitch
 * while making the tall one feel stuck.
 */
const DISMISS_RATIO = 0.3;

/**
 * Movement below this is not a drag. Without it, every tap on the drag handle
 * fires a `pointerdown`→`pointerup` with a few px of jitter from the thumb and
 * the sheet visibly twitches before springing back.
 */
const DRAG_SLOP_PX = 4;

/**
 * W48.0 — what counts as "the user pressed a control" for the drag guard.
 *
 * `label` is included because a `<label>` wraps its input, and a tap on the
 * label's text is a tap on the control. `summary`/`details` are drag-ish but a
 * sheet header has no reason to hold one, and leaving them out keeps the
 * predicate to things that genuinely take a click.
 */
const INTERACTIVE =
  "button, a[href], input, select, textarea, label, [role='button'], [contenteditable='true']";

/**
 * W46.0 — swipe-to-dismiss, which DESIGN.md §11.5 #5 and §18.7 have required
 * since W33.2 and which `BottomSheet` had never implemented.
 *
 * Every sheet in the product closed in exactly three ways: the ✕, the backdrop,
 * and Escape. On a phone the third does not exist, and the other two both
 * require the user to aim at a target — while the one gesture a thumb already
 * knows (drag the thing down) did nothing.
 *
 * Three decisions worth stating, because each is a place the obvious version is
 * wrong:
 *
 * **Pointer events, not touch events.** The obvious implementation is
 * `onTouchStart/Move/End`. That gives a sheet that cannot be dismissed by drag
 * on a touch-enabled laptop or a desktop browser in device-emulation mode, and
 * it carries no button-state check, so a right-click drag starts one. Pointer
 * events cover mouse / touch / pen with one path.
 *
 * **The offset rides a CSS custom property, never `style.transform`.** §16 bans
 * inline `style.transform` ("state 走 `data-open` attribute"), and the literal
 * reading is right: the transform is *declared* in `globals.css` and JS only
 * writes the number. So the sheet still has exactly one place where geometry is
 * decided, and `--sheet-drag` is a channel rather than a second source.
 *
 * **The gesture starts only when the sheet is scrolled to the top.** Otherwise
 * dragging down to read the bottom of a long list yanks the sheet closed
 * instead of scrolling it — the single most common way a drag-to-dismiss gets
 * shipped and the first thing anyone tries.
 */
function useSheetDrag(
  open: boolean,
  enabled: boolean,
  onDismiss: () => void,
  detent: SheetDetent,
  onDetentChange: (next: SheetDetent) => void,
) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  // The element that actually carries the handlers — the drag zone, not the
  // sheet. Pointer capture retargets the whole pointer stream to the capturing
  // element, so capturing on the sheet while listening on the child means the
  // second `pointermove` onward is dispatched at the sheet and never reaches the
  // handler: the sheet moved 9px on the first event and then stopped dead, and
  // the release never ran so it stayed displaced. Capturing on the same element
  // that listens is the whole contract.
  const zoneRef = useRef<HTMLDivElement | null>(null);
  const pointerId = useRef<number | null>(null);
  const startY = useRef(0);
  const offset = useRef(0);
  const dragging = useRef(false);
  // The gesture closes the sheet. Held in a ref so that a parent re-render
  // cannot swap the callback out from under an in-flight pointer capture, and
  // so the three handlers below stay referentially stable for the gesture's
  // lifetime.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  // Same reason as `dismissRef`: the detent callback must not be swapped out
  // from under an in-flight gesture.
  const detentRef = useRef(onDetentChange);
  detentRef.current = onDetentChange;
  const detentValueRef = useRef(detent);
  detentValueRef.current = detent;

  const setOffset = useCallback((px: number) => {
    offset.current = px;
    // `--sheet-drag`, not `transform`. See the note above.
    sheetRef.current?.style.setProperty("--sheet-drag", `${px}px`);
  }, []);

  /**
   * The live height cap while the sheet is being pulled open.
   *
   * `--sheet-height` is the **cap** — the same variable the `data-detent`
   * rules set — and it is the only channel the growth uses. A bottom-anchored
   * sheet grows by raising its cap, which moves its top edge up by exactly the
   * growth, so the grabber stays under the finger with **no transform at all**.
   *
   * That is why `--sheet-drag` and `--sheet-height` do not fight: the drag
   * channel is clamped at zero in the stylesheet, so a downward drag slides
   * the sheet and an upward drag grows it, and only one of them is ever
   * non-zero.
   */
  const setHeight = useCallback((px: number | null) => {
    const el = sheetRef.current;
    if (!el) return;
    if (px === null) el.style.removeProperty("--sheet-height");
    else el.style.setProperty("--sheet-height", `${px}px`);
  }, []);

  const end = useCallback(
    () => {
      const el = sheetRef.current;
      // A press that never crossed the slop was a tap, not a drag: it must not
      // dismiss, must not leave `--sheet-drag` set, and — since no capture was
      // ever taken — must leave the click on whatever was pressed intact.
      if (!dragging.current || !el) {
        dragging.current = false;
        pointerId.current = null;
        return;
      }
      dragging.current = false;
      const id = pointerId.current;
      pointerId.current = null;
      el.removeAttribute("data-dragging");
      if (id !== null) {
        // Symmetric with the capture above: `releasePointerCapture` throws on an
        // id that was never captured, and this runs on every release including
        // the ones where capture failed.
        try {
          zoneRef.current?.releasePointerCapture?.(id);
        } catch {
          /* never captured — nothing to release */
        }
      }
      const delta = offset.current;
      // The dismiss rule is unchanged: DESIGN.md §11.5 #5 — 「拖到 sheet-bottom
      // 30% 处 release 关闭」. It is measured against the sheet's own height,
      // and only a **downward** drag can reach it.
      if (delta >= el.offsetHeight * DISMISS_RATIO) {
        setHeight(null);
        dismissRef.current();
        return;
      }
      // W56.0 B (ADR-0198) — snap to the nearest detent instead of only
      // "dismiss or return to where you were".
      const viewport = typeof window === "undefined" ? 0 : window.innerHeight;
      detentRef.current(snapDetent(detentValueRef.current, delta, viewport));
      setHeight(null);
      setOffset(0);
    },
    [setHeight, setOffset],
  );

  useEffect(() => {
    if (!open) {
      // A sheet that closed mid-drag (Escape, backdrop) must not keep its
      // offset, or it reopens displaced.
      setOffset(0);
      setHeight(null);
      dragging.current = false;
      pointerId.current = null;
    }
  }, [open, setOffset, setHeight]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      // A sheet scrolled anywhere but the top drags the list, not the sheet.
      const el = sheetRef.current;
      if (el && el.scrollTop > 0) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      // **W48.0** — a press that lands on an interactive control is a tap on
      // that control, never the start of a drag.
      //
      // This is the whole fix for 「点一次关不掉，要点多次」. The ✕ lives inside
      // this drag zone (`BottomSheetDragZone`), and the zone is `touch-none`
      // plus `setPointerCapture`: any movement of `DRAG_SLOP_PX` (4px) between
      // down and up armed the drag, captured the pointer, and retargeted the
      // resulting click to the zone — so `Dialog.Close` never received it. A
      // thumb resting on a 44px target drifts 4px routinely, which is why it
      // read as "sometimes the first tap does nothing" rather than as a broken
      // button. The calendar was the worst case for a reason worth recording:
      // its day sheet opens from a day *cell* tap, so the finger is already
      // travelling when the sheet appears under it.
      //
      // The guard is in the gesture, not in the markup. Every sheet in the
      // product shares this one component (20 call sites), and any control a
      // future sheet drops into its header is covered without remembering to
      // annotate it — which is the property the alternative (moving the ✕ out
      // of the zone) does not have.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.(INTERACTIVE)) return;
      pointerId.current = e.pointerId;
      startY.current = e.clientY;
      // `dragging` stays **false** here, and no pointer capture is taken.
      //
      // Capturing on pointerdown was the first version and it broke every button
      // inside a sheet. Capture retargets the whole pointer stream to the
      // capturing element, so a `pointerdown` that bubbled up from
      // `mood-option-happy` moved that button's subsequent `click` to the sheet
      // and the tap did nothing. It surfaced as
      // `Dashboard.test.tsx › writes a level-3 code …` failing with the write spy
      // never called at all.
      //
      // A gesture only becomes a drag once it has travelled (onPointerMove), and
      // by then the movement has cancelled the click on its own — which is the
      // right outcome, because a 3px "tap" was never a tap.
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (pointerId.current === null || e.pointerId !== pointerId.current) return;
      const delta = e.clientY - startY.current;

      if (!dragging.current) {
        // W56.0 B (ADR-0198) — **both directions** arm the drag.
        //
        // It used to be downward-only, and the reason was sound at the time:
        // the gesture lived on the sheet, where an upward swipe means "scroll
        // the content", and capturing the pointer for it would take the gesture
        // away from the scroller.
        //
        // The handlers are on the **drag zone** — the handle bar and the sheet
        // header — which is `touch-none` and not scrollable. There is no
        // content up there to scroll, so an upward flick in the zone can only
        // mean "give me more room", and the sheet's answer is to grow to the
        // next detent.
        if (Math.abs(delta) < DRAG_SLOP_PX) return;
        dragging.current = true;
        // `setPointerCapture` **throws** `NotFoundError` when the pointerId does
        // not correspond to an active pointer — a pointer released between the
        // down and the move, or a synthetic event from a test. The `?.` guard
        // only covers the method being absent, not it failing, and an exception
        // here propagates straight out of the handler and aborts it before
        // `setOffset` — which is exactly how the drag came out inert in a real
        // browser while passing every jsdom test. Capture is an optimisation for
        // a gesture that already works; losing it costs the drag nothing.
        try {
          zoneRef.current?.setPointerCapture?.(e.pointerId);
        } catch {
          /* no active pointer — the gesture still tracks via pointermove on the zone */
        }
      }

      setOffset(delta);
      sheetRef.current?.setAttribute("data-dragging", delta < 0 ? "up" : "down");
      // The growth channel only exists while pulling **open**. A downward drag
      // slides the sheet instead (the transform clamps `--sheet-drag` at zero
      // for the upward case), and it deliberately leaves the cap alone: the
      // sheet shrinks into its detent when the gesture ends, not while it is
      // still in the user's hand.
      if (delta < 0) {
        const viewport = typeof window === "undefined" ? 0 : window.innerHeight;
        setHeight(detentHeightPx(detentValueRef.current, viewport) + Math.abs(delta));
      }
      e.preventDefault();
    },
    [setHeight, setOffset],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerId !== pointerId.current) return;
      end();
    },
    [end],
  );

  return { sheetRef, zoneRef, onPointerDown, onPointerMove, onPointerUp };
}

export type BottomSheetVariant = "mobile" | "desktop-modal" | "desktop-side";

export interface BottomSheetProps {
  /** Controlled open state. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: BottomSheetVariant;
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
  /**
   * W45.0. Defaults to **true** — a sheet with no visible exit is a trap.
   *
   * Opt out only where the sheet is a decision the user cannot walk away from
   * without answering, and even then Escape and the overlay still work. Any call
   * site passing `false` should say in a comment which decision it is protecting,
   * because the flag's whole cost is that it looks like a way to tidy the header.
   */
  showClose?: boolean;
  /**
   * W56.0 I — whether a tap on the backdrop closes the sheet. Defaults to
   * **true**, which is the right default for a sheet whose content is *read*
   * (「相簿封面挑選」, 「通知清單」, 「日詳情」): tapping outside is the
   * expected way out.
   *
   * A sheet that holds a **form** is different. The tap is very often a thumb
   * that was aiming at something else, and losing what was typed to a mis-tap
   * is the single worst thing a form can do — so those call sites pass
   * `false`.
   *
   * **Escape always closes**, whatever this is set to. Escape is a deliberate
   * 「I want out」, not a mis-tap, and `DESIGN.md §11.1 #5` lists it as a
   * closing gesture — so it is deliberately not intercepted here.
   */
  dismissibleOnBackdrop?: boolean;
  /**
   * W58.3 — the detent the sheet rests at **when it opens**.
   *
   * Defaults to `DEFAULT_DETENT` (`medium`), which is what every sheet in the
   * product has done since W56.0 B (ADR-0198).
   *
   * **Why a sheet needs to ask for a different one.** A detent is a *cap*, so
   * a short sheet at `large` still renders at its content height — the detent
   * only matters when there is enough content to fill it. The day sheet is
   * the case that needs it: with the cycle projection in it (phase, countdown
   * numbers, the daily log form) the content is two screens long, and at
   * `medium` (60vh) the user opens a sheet and immediately has to drag it
   * taller before they can read what they came for. `large` (90vh) opens it
   * at the size the content actually wants.
   *
   * The sheet still moves between detents by drag and by the grabber — this
   * only chooses where it *starts*.
   */
  initialDetent?: SheetDetent;
  /**
   * Stable selector for tests. BottomSheet renders through a Radix Portal, so
   * callers cannot wrap it to get a handle on the sheet surface — several
   * W36.0 sheets (message editor / mood picker / notification inbox) assert on
   * their own presence this way.
   */
  "data-testid"?: string;
}

/**
 * PA1 — BottomSheet (3 variants).
 *
 * Per `component-api.md §3 PA1` + `§5`:
 *   - **mobile**: bottom-anchored, full-width × 50-90vh, slide-up 280ms `ease-quiet`
 *   - **desktop-modal**: centered, 520px wide, fade+scale 200ms `ease-quiet`
 *   - **desktop-side**: right slide-right 280ms `ease-quiet`, 320px wide
 *   - 全部 `--glass-1`
 *   - State via `data-open` attribute — never inline `style.transform`
 *
 * Built on Radix Dialog primitive (per w1.md §4.1 dependency choice).
 */
export function BottomSheet({
  open,
  onOpenChange,
  variant = "desktop-modal",
  title,
  description,
  children,
  className,
  showClose = true,
  dismissibleOnBackdrop = true,
  initialDetent = DEFAULT_DETENT,
  "data-testid": testId,
}: BottomSheetProps) {
  // Only the mobile variant gets a drag handle, per DESIGN.md §11: the desktop
  // modal is centred and the side panel is a drawer — dragging either downward
  // is not a gesture the user has any reason to attempt.
  const draggable = variant === "mobile";
  // W56.0 B (ADR-0198) — the detent the sheet rests at. `medium` on open, per
  // DESIGN.md §11.1 #2 (「Medium 預設 → Large 可拖」), which **supersedes**
  // §18.7's 「不分 small/medium」 — the two sections contradicted each other
  // and the code implemented neither.
  //
  // The desktop variants have no resting height to report, so they emit no
  // `data-detent` at all and this state is simply unused for them.
  //
  // W58.3 — the opening detent is the caller's choice (`initialDetent`); the
  // default is still `medium` for every sheet that does not ask.
  const [detent, setDetent] = useState<SheetDetent>(initialDetent);
  // A sheet re-opens at its opening detent, like iOS: the position a sheet was
  // left in is a property of that presentation, not of the sheet.
  useEffect(() => {
    if (open) setDetent(initialDetent);
  }, [open, initialDetent]);
  // W56.0 E (ADR-0199) — **the focus goes back to whatever opened the sheet.**
  //
  // A controlled `Dialog.Root` has no `Dialog.Trigger`, so Radix has nowhere to
  // restore the focus to: the sheet closed and the focus landed on `<body>`,
  // and a keyboard user had to start the page over. This is the fix for all
  // 20 `BottomSheet` call sites **and** the 9 `ConfirmDialog` ones that inherit
  // it — one place, not thirty-seven.
  //
  // The capture happens during render, not in an effect: Radix moves focus
  // into the dialog in an effect, and React runs a child's effects before its
  // parent's, so by the time an effect here ran the opener would already be
  // unfocused. See `lib/a11y/focusRestore.ts`.
  const focusTargetRef = useFocusRestoreTarget(open);
  const { sheetRef, zoneRef, onPointerDown, onPointerMove, onPointerUp } = useSheetDrag(
    open,
    draggable,
    () => onOpenChange(false),
    detent,
    setDetent,
  );
  // W50.2 — a pointer tap on the ✕ fires `pointerup` **and** (when the browser
  // synthesises one) `click`. The first closes the sheet; the second must not
  // run against a sheet that is already gone. Cleared whenever the sheet
  // re-opens, so the next press starts clean.
  const closeGuardRef = useRef(false);
  useEffect(() => {
    if (open) closeGuardRef.current = false;
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          data-component="BottomSheetOverlay"
          data-variant={variant}
          // The fade in/out used to be `data-[state=open]:animate-in
          // data-[state=open]:fade-in-0` + the `animate-out` pair. Those
          // utilities come from `tailwindcss-animate`, which is not installed
          // and `tailwind.config.ts` has `plugins: []` — so they emitted **no
          // rule at all** and the backdrop appeared instantly. The motion now
          // lives in `globals.css` against this component's `data-state`, using
          // the project's own `--dur-*` / `--ease-quiet` tokens (DESIGN §11.1 #7:
          // 「ease-quiet 280ms slide-up + 200ms backdrop fade-in」).
          className={cn(
            "fixed inset-0 z-50 bg-ink-1/30 backdrop-blur-sm",
          )}
        />
        <Dialog.Content
          ref={sheetRef}
          data-component="BottomSheet"
          data-variant={variant}
          data-open={open}
          // W56.0 E (ADR-0199) — give the focus back to the opener. Radix's own
          // `onCloseAutoFocus` focuses the *trigger*, which a controlled dialog
          // does not have; this restores the element that actually had focus.
          onCloseAutoFocus={(event) => restoreFocus(event, focusTargetRef.current)}
          // W56.0 I — a form sheet refuses to be dismissed by a stray tap on
          // the backdrop. `onInteractOutside` is the seam Radix offers for it;
          // Escape is left alone on purpose (see the prop's note).
          onInteractOutside={(event) => {
            if (!dismissibleOnBackdrop) event.preventDefault();
          }}
          // DESIGN §11.1 #4 names `data-open` + `data-detent` as the two state
          // attributes a sheet carries, and §18.7 records the value as `large`
          // for every sheet (「不分 small/medium」). The component emitted
          // neither — a `detent` search over `frontend/src` returned only
          // comments, four of which described a **medium** detent that has
          // never existed. The attribute is emitted for the mobile variant
          // only: a detent is the height a bottom-anchored sheet rests at, and
          // the desktop modal (centred) and side drawer (full-height) have no
          // such state to report.
          //
          // W56.0 B (ADR-0201 → ADR-0198): the value is now **real** — three
          // detents, `medium` on open, and the height each one means lives in
          // `globals.css` keyed on this attribute.
          data-detent={variant === "mobile" ? detent : undefined}
          data-testid={testId}
          className={cn(
            // W44: `surface-sheet` rather than `glass-1`. `--glass-1-bg` is
            // `rgb(252 250 245 / .94)` — the warm paper colour — so a sheet over
            // the wash composited to a warm off-white and read as cream. The
            // frosted blur is unchanged; only the tint moved to pure white.
            "fixed z-50 surface-sheet focus:outline-none",
            "shadow-modal",
            // Content taller than the sheet must be reachable. `max-h-[90vh]`
            // without a scroll container silently *clips* it: on a 390×844
            // phone the notification list's last rows sit below the fold with
            // no way to get to them, and nothing looks broken.
            "overflow-y-auto overscroll-contain",
            // **`touch-pan-y`, not `touch-none`.** This element is the scroller,
            // and `touch-action: none` on a scroll container tells the browser
            // not to use touch for panning *it* — so the two declarations were in
            // direct conflict and the sheet's own comment claimed the scroller
            // "keeps `touch-action: pan-y`" while no rule provided one.
            //
            // It was invisible in every audit because the demo data fits: the
            // notification inbox measured `scrollHeight 475 <= clientHeight 475`
            // and the mood picker `302 <= 302`, so no sheet ever overflowed and
            // the pan never had to happen. The defect is latent, not absent —
            // any sheet with more content than fits (a longer list, a bigger
            // form) becomes **unscrollable on touch**, which is a dead end
            // rather than a layout bug: the content is right there and cannot be
            // reached.
            //
            // Vertical panning is exactly what a sheet's content needs, so this
            // is not a compromise. The gesture that must not scroll is the drag,
            // and that is confined to the drag zone below, which is `touch-none`.
            variant === "mobile" && "touch-pan-y",
            // W56.0 B (ADR-0198) — the mobile sheet's `max-h-[90vh]` is **gone**,
            // replaced by the detent cap in `globals.css`
            // (`max-height: var(--sheet-height)`, which `data-detent` sets to
            // 30vh / 60vh / 90vh).
            //
            // It could not stay as a Tailwind utility: a utility is one value,
            // and the whole point of a detent is that the value changes with
            // the state. The rule lives in `globals.css` because that is where
            // the sheet's geometry already lives (the transform combination
            // point) — one place decides how tall the sheet is.
            variant === "mobile" &&
              "bottom-0 inset-x-0 rounded-t-lg p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]",
            variant === "desktop-modal" &&
              "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] max-w-[92vw] max-h-[90vh] rounded-lg p-6",
            variant === "desktop-side" && "right-0 top-0 bottom-0 w-[320px]",
            className,
          )}
        >
          {/*
            W46.0 — the drag zone: handle bar + sheet header, which is what
            DESIGN.md §18.7 specifies（「handle bar + sheet-header 任意位置」）.

            The gesture handlers live here rather than on `Dialog.Content` for
            two reasons, and the second is the one that matters.

            **Spec.** §18.7 names the handle and the header. The first version put
            them on the whole sheet, which is a superset — a drag could start on
            body content, where a vertical swipe is far more likely to have been
            meant as "scroll this" or "swipe this row".

            **`touch-none` belongs here.** The gesture has to beat the browser's
            scroll gesture, and that requires `touch-action: none` on the element
            carrying the handlers. On the scroller that declaration also disabled
            the panning it was meant to coexist with. Scoped to a 44px-tall strip,
            `touch-none` costs nothing: the header is not scrollable content, so
            there is nothing there for a pan to do.
          */}
          <div
            ref={zoneRef}
            data-component="BottomSheetDragZone"
            data-drag-zone={draggable ? "on" : undefined}
            onPointerDown={draggable ? onPointerDown : undefined}
            onPointerMove={draggable ? onPointerMove : undefined}
            onPointerUp={draggable ? onPointerUp : undefined}
            onPointerCancel={draggable ? onPointerUp : undefined}
            className={cn("shrink-0", draggable && "touch-none")}
          >
          {/*
            W46.0 — the drag handle.

            A `paper-line` rule, not a filled bar: DESIGN.md §11.1 #3 specifies a
            line, and a filled 4px pill reads as a control the user is meant to
            press.

            It was `aria-hidden` because it was not an interactive element — it
            carried no role, no tab stop, no handler of its own. A `role="button"`
            there would announce a control that does nothing when activated by a
            screen reader, which is the same defect `MonthGrid`'s overflow count
            was fixed for (ADR-0078).

            W56.0 B (ADR-0198) — **that reason expired.** The grabber now has a
            behaviour of its own: Apple's `sheets.md` says 「tap it to cycle
            through the detents」, and a control that announces nothing and does
            nothing is worse than a control that announces what it will do. So it
            is a real `<button>`: it cycles the detent, it takes a tab stop, it
            says what it does, and `aria-expanded` says whether the sheet is
            currently collapsed.

            **It is also excluded from the drag**, automatically: the drag's
            `INTERACTIVE` guard refuses to arm on a press that lands on a
            `button`, so a tap on the grabber cycles instead of dragging the
            sheet 4px. That is why the guard list has always included `button`
            and why the handle had to become one rather than carrying a
            `role="button"`.

            The hit area is 44px tall (`after:-top-[10px]` / `after:-bottom-[10px]`
            on a 24px button) while the drawn bar stays the 4px rule §11.1 #3
            asks for. The extension above the button stays inside the sheet's
            own 20px padding, so it is not clipped by the sheet's scroller.
          */}
          {draggable ? (
            <button
              type="button"
              data-testid="bottom-sheet-handle"
              data-detent={detent}
              aria-label={detent === "large" ? "收合面板" : "展開面板"}
              aria-expanded={detent !== "collapsed"}
              onClick={() => setDetent((current) => nextDetent(current))}
              className="sheet-grabber"
            >
              <span aria-hidden className="h-1 w-10 rounded-full bg-hairline-strong" />
            </button>
          ) : null}

          {/*
            W45.0 — an explicit close control, for all 20 call sites.

            Radix supplies dismissal *semantics* (Escape, overlay click) but
            renders no visible control, and a count of `Dialog.Close` across
            `src` was **zero** while 20 sheets shipped. So every sheet in the
            product closed in exactly two ways, both of which require knowing a
            sheet is open and guessing where its edge is. For a bottom-anchored
            sheet the dismissible zone is the ~48px strip above its top edge, and
            Apple's own guidance is not to rely on that as the only exit.

            It lives in the primitive rather than in each call site because 20
            hand-written close buttons is 20 chances to ship one with a 32px
            target and no `aria-label`.

            The header is a real row and **reserves 44px whether or not there is
            a title**, so adding the button cannot shift a sheet that already
            had a title — and a sheet without a title gets the same geometry
            rather than a title-only layout.
          */}
          <div className="relative flex min-h-11 items-center pr-11">
            {title ? (
              <Dialog.Title className="text-h2 text-ink-1">{title}</Dialog.Title>
            ) : (
              <Dialog.Title className="sr-only">{title ?? "Bottom sheet"}</Dialog.Title>
            )}
            {showClose ? (
              <Dialog.Close
                // `Dialog.Close` closes through Radix, so the controlled
                // `onOpenChange` still fires — which matters because several
                // call sites clear their own state there.
                data-testid="bottom-sheet-close"
                aria-label="關閉"
                // `sheet-close`, not `shell-btn`: that class claims 44px of height
                // and leaves its width at 38px, which is fine for a bell in a
                // 44px topbar row and wrong for a sheet's only visible exit.
                className="sheet-close press"
                // **W50.2 — close on `pointerup`, not only on `click`.**
                //
                // Two rounds of measurement, and the second one overturned the
                // first. Recorded here because the wrong answer was plausible
                // enough to ship twice.
                //
                // **ADR-0144 is not the mechanism.** Its guard (don't arm the
                // drag when the press lands on a control) genuinely works —
                // `data-dragging` is `n` through every tap at every drift. It
                // guards the wrong half.
                //
                // **What actually happens.** Measured on a 390×844 touch context,
                // tapping the ✕ with a drifting thumb:
                //
                //     drift  0px → closes on the first tap
                //     drift  8px → closes on the first tap
                //     drift 16px → **never closes**, 6/6 rounds
                //
                // The event log says why: at 16px Chrome emits `pointerdown`
                // and `pointerup` and then **no `click` at all**. Past the touch
                // slop the browser stops synthesising clicks, and that is a
                // browser rule — no amount of gesture guarding reaches it, which
                // is why the ADR-0144 fix could look correct, be correct for the
                // cases it covers, and still leave 「要多點幾次」 open.
                //
                // **Pointer capture is not the answer either.** It was tried and
                // measured: capture retargets `pointerup` onto the button while
                // `pointerdown` naturally landed on the `<path>` inside it, so
                // the two targets disagreed and the click was lost — it made the
                // 16px case fail for a *second, new* reason.
                //
                // Acting on `pointerup` is deterministic and independent of
                // whether the browser chooses to synthesise a click. The click
                // handler is kept for keyboard and assistive technology, which
                // produce no pointer events at all; a pointer tap fires both, so
                // `closeGuardRef` makes the second one a no-op rather than
                // closing a sheet that is already gone.
                onPointerUp={() => {
                  if (closeGuardRef.current) return;
                  closeGuardRef.current = true;
                  onOpenChange(false);
                }}
                onClick={(e) => {
                  // The guard above is useless on its own: `Dialog.Close` has its
                  // **own** click handler that calls `onOpenChange(false)`
                  // directly, so a pointer tap produced two closes. Radix composes
                  // its handler with `composeEventHandlers`, which skips the
                  // original when `defaultPrevented` is set — so that is the seam
                  // to use. Keyboard and assistive-technology activation never
                  // fired a `pointerup`, so the guard is still false and the
                  // click path closes normally.
                  if (closeGuardRef.current) e.preventDefault();
                }}
              >
                <X size={18} strokeWidth={2.2} aria-hidden />
              </Dialog.Close>
            ) : null}
          </div>
          {/* End of the drag zone — handle + header only. Sheet content below is
              scrollable and must not start a drag. */}
          </div>
          {description ? (
            <Dialog.Description className="text-body-sm text-ink-2 mb-4">
              {description}
            </Dialog.Description>
          ) : null}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
